import {
    Animation,
    AnimationComponent,
    Component,
    MeshComponent,
    Object3D,
} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import {Player, PlayerStates} from '../state/Player.js';
import {RNG} from '../utils/rng.js';
import {WeaponSlot} from './weapon-slot.js';
import {Constants} from '../state/data/constants.js';
import {gameState} from '../state/GameState.js';

export class PlayerAnimationController extends Component {
    static TypeName = 'player-animation-controller';

    @property.object({required: true})
    meshObject!: Object3D;

    @property.animation()
    animIdle: Animation | null = null;
    @property.animation()
    animRun: Animation | null = null;
    @property.animation()
    animHurt: Animation | null = null;
    @property.animation()
    animAttack: Animation | null = null;
    @property.animation()
    animAttack2: Animation | null = null;
    @property.animation()
    animAttack3: Animation | null = null;

    @property.animation()
    animAttack1HWeapon: Animation | null = null;
    @property.animation()
    animAttack1HWeapon2: Animation | null = null;
    @property.animation()
    animAttack1HWeapon3: Animation | null = null;

    @property.animation()
    animAttack2HWeapon: Animation | null = null;
    @property.animation()
    animAttack2HWeapon2: Animation | null = null;
    @property.animation()
    animAttack2HWeapon3: Animation | null = null;

    @property.animation()
    animDeath: Animation | null = null;
    @property.animation()
    animDash: Animation | null = null;
    @property.animation()
    animPower: Animation | null = null;

    private _playerData: Player | null = null;
    private _animation: AnimationComponent | null = null;
    private _weaponSlot: WeaponSlot | null = null;

    // Add this new property to track attack animation end time
    private _attackAnimationEndTime: number | null = null;

    start() {
        this._animation = this.object.getComponent(AnimationComponent);
        this._weaponSlot = this.object.getComponent(WeaponSlot);
        const skin = this.meshObject.getComponent(MeshComponent)?.skin;
        if (skin) {
            this.animIdle = this.animIdle?.retarget(skin) ?? null;
            this.animRun = this.animRun?.retarget(skin) ?? null;
            this.animHurt = this.animHurt?.retarget(skin) ?? null;
            this.animAttack = this.animAttack?.retarget(skin) ?? null;
            this.animAttack2 = this.animAttack2?.retarget(skin) ?? null;
            this.animAttack3 = this.animAttack3?.retarget(skin) ?? null;
            this.animDeath = this.animDeath?.retarget(skin) ?? null;
            this.animDash = this.animDash?.retarget(skin) ?? null;
            this.animPower = this.animPower?.retarget(skin) ?? null;
        }
    }

    onActivate(): void {
        this._animation?.onEvent.add(this._onAnimationEvent);
    }
    onDeactivate(): void {
        this._animation?.onEvent.remove(this._onAnimationEvent);
    }

    private _onAnimationEvent = (animationEvent: string) => {
        if (animationEvent === 'Death_A_Ended') {
            gameState.currentPlayer.setState(PlayerStates.Dead);
        }
    };

    update(dt: number) {
        // Check if the attack animation end time is set and has passed
        if (this._attackAnimationEndTime !== null) {
            if (Date.now() >= this._attackAnimationEndTime) {
                this._playerData?.attackStateReset();
                this._attackAnimationEndTime = null; // Reset the timer
            }
        }
    }

    setPlayerData(playerData: Player) {
        if (this._playerData != null) {
            console.warn('Player data is already set.');
            return;
        }
        this._playerData = playerData;
        this._playerData.propertyChanged.add(this._onPlayerPropertyChange);

        if (this._animation) {
            this._animation.active = true;
        }
    }

    private _onPlayerPropertyChange = (property: string) => {
        if (property === 'state') {
            const hasWeapon = this._weaponSlot?.hasWeapon;
            switch (this._playerData!.state) {
                case PlayerStates.Idle:
                    this._animation!.animation = this.animIdle;
                    this._animation!.playCount = 0;
                    this._animation!.speed = 1;
                    break;
                case PlayerStates.Move:
                    this._animation!.animation = this.animRun;
                    this._animation!.playCount = 0;
                    this._animation!.speed = 1.4;
                    break;
                case PlayerStates.Hit:
                    this._animation!.animation = this.animHurt;
                    this._animation!.playCount = 1;
                    break;
                case PlayerStates.Attack:
                    switch (RNG.getUniformInt(0, 2)) {
                        case 0:
                            this._animation!.animation = hasWeapon
                                ? this.animAttack1HWeapon
                                : this.animAttack;
                            break;
                        case 1:
                            this._animation!.animation = hasWeapon
                                ? this.animAttack1HWeapon2
                                : this.animAttack2;
                            break;
                        case 2:
                            this._animation!.animation = hasWeapon
                                ? this.animAttack1HWeapon3
                                : this.animAttack3;
                            break;
                    }

                    this._animation!.playCount = 1;
                    this._animation!.speed = 2;
                    const animationDuration =
                        (this._animation?.animation?.duration! / this._animation?.speed!) *
                        1000;
                    Constants.attackAnimationDuration = animationDuration;

                    // Set the attack animation end time
                    this._attackAnimationEndTime = Date.now() + animationDuration;
                    break;
                case PlayerStates.Dying:
                    this._animation!.animation = this.animDeath;
                    this._animation!.speed = 1;
                    this._animation!.playCount = 1;
                    break;
                case PlayerStates.Dashing:
                    this._animation!.animation = this.animDash;
                    this._animation!.speed = 1;
                    this._animation!.playCount = 0;
                    break;
                case PlayerStates.Power:
                    this._animation!.animation = this.animPower;
                    this._animation!.speed = 1.5;
                    this._animation!.playCount = 1;
                    break;

                case PlayerStates.Dead:
                // Don't animate when dead.
                default:
                    // Unlisted state, do not animate anything
                    return;
            }
            this._animation!.play();
        }
    };
}
